import React from 'react';
import { Navigate, useLocation, useOutlet } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuthStore } from '../../stores/authStore';
import { useCurrentUserPrivileges } from '../../hooks/useUserPrivileges';
import navigationConfig from '../../config/navigationConfig.json';

// Build path -> privilegeKey lookup from navigation config
const pathToPrivilegeKey = {};
navigationConfig.sections.forEach((section) => {
  section.items.forEach((item) => {
    if (item.privilegeKey) pathToPrivilegeKey[item.path] = item.privilegeKey;
    if (item.children) {
      item.children.forEach((child) => {
        if (child.privilegeKey) pathToPrivilegeKey[child.path] = child.privilegeKey;
      });
    }
  });
});

// Find the privilegeKey for a given pathname (handles sub-paths like /bom/123)
const getPrivilegeKeyForPath = (pathname) => {
  // Exact match first
  if (pathToPrivilegeKey[pathname]) return pathToPrivilegeKey[pathname];
  // Try matching base path (e.g. /bom/123 -> /bom, /invoices/edit/5 -> /invoice)
  const configPaths = Object.keys(pathToPrivilegeKey);
  for (const configPath of configPaths) {
    if (pathname.startsWith(configPath + '/')) return pathToPrivilegeKey[configPath];
  }
  return null;
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();
  const location = useLocation();
  const { hasPrivilege, isLoading: privilegesLoading } = useCurrentUserPrivileges();

  // Show loading spinner while checking authentication
  if (isLoading || privilegesLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // Redirect to sign-in if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // Render protected content
  return children;
};

const PrivilegeGuard = () => {
  const location = useLocation();
  const outlet = useOutlet();
  const { user } = useAuthStore();
  const { hasPrivilege, isLoading: privilegesLoading } = useCurrentUserPrivileges();

  if (privilegesLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  // SUPER_ADMIN bypasses all privilege checks
  const isSuperAdmin = user?.roles?.includes("SUPER_ADMIN");
  if (isSuperAdmin) return outlet;

  const privilegeKey = getPrivilegeKeyForPath(location.pathname);

  // No privilege required for this route (e.g. /dashboard, /profile) - allow access
  if (!privilegeKey) return outlet;

  // Check if user has the required privilege
  if (!hasPrivilege(privilegeKey)) {
    return <Navigate to="/dashboard" replace />;
  }

  return outlet;
};

export default ProtectedRoute;
export { PrivilegeGuard };
